/**
 * This file includes functions that evaluate the layer style rules for a given feature and return the correct styleRule to apply.
 */
import { deg2rad } from 'client/pages/MapPage/layers/customVectorHelpers';
import { mapMenuTabKeys } from 'client/utils/constants/appStates';
import { defaultSectorSpecificStyles, sectors } from 'client/store/layersSlice/layersSlice';
const isolatedViewsMapMode = mapMenuTabKeys.ISOLATED_VIEWS;

const ROTATION_UNITS = {
    deg: 'Degrees',
    rad: 'Radians'
};

export const ROTATION_TYPES = {
    geographic: 'Geographic',
    arithmetic: 'Arithmetic'
};

export const normaliseFeatureValue = (featureValue, expectedValue) => {
    if (featureValue === null || featureValue === undefined) {
        return featureValue;
    }

    if (typeof expectedValue === 'string') {
        return String(featureValue);
    }

    if (typeof expectedValue === 'number') {
        const numericFeatureValue = Number(featureValue);

        if (Number.isNaN(numericFeatureValue)) {
            return featureValue;
        }

        return numericFeatureValue;
    }

    return featureValue;
};

/**
 * Use numeric comparison when both values are numeric-like to avoid lexicographic string checks
 * (for example, '33000' <= '460' should be evaluated as 33000 <= 460).
 * there was an issue where '33000' <= '460' was returning true because it was being evaluated as a string comparison.
 */
const evaluateRelationalCondition = (normalisedValue, expectedValue, comparator) => {
    const numericNormalisedValue = Number(normalisedValue);
    const numericExpectedValue = Number(expectedValue);
    const useNumericComparison = !Number.isNaN(numericNormalisedValue) && !Number.isNaN(numericExpectedValue);

    // if both are numbers, compare as numbers, otherwise compare as strings
    if (useNumericComparison) {
        return comparator(numericNormalisedValue, numericExpectedValue);
    }

    return comparator(normalisedValue, expectedValue);
};

// evaluate nested conditions
const evaluateCondition = (feature, condition) => {
    const { field, value: expectedValue } = condition;
    const featureValue = feature.get(field);
    const normalisedValue = normaliseFeatureValue(featureValue, expectedValue);

    switch (condition.operator) {
        case 'and':
            return condition.conditions.every((subCondition) => evaluateCondition(feature, subCondition));
        case 'or':
            return condition.conditions.some((subCondition) => evaluateCondition(feature, subCondition));
        case '=':
            return normalisedValue === expectedValue;
        case '!=':
            return normalisedValue !== expectedValue;
        case '<':
            return evaluateRelationalCondition(normalisedValue, expectedValue, (left, right) => left < right);
        case '>':
            return evaluateRelationalCondition(normalisedValue, expectedValue, (left, right) => left > right);
        case '<=':
            return evaluateRelationalCondition(normalisedValue, expectedValue, (left, right) => left <= right);
        case '>=':
            return evaluateRelationalCondition(normalisedValue, expectedValue, (left, right) => left >= right);
        default:
            return false;
    }
};

const getSectorFromFeature = (feature) => {
    const featureId = feature?.getId?.();
    const assetType = typeof featureId === 'string' ? featureId.split('.')[0] : null;

    if (assetType?.startsWith(sectors.GAS)) {
        return sectors.GAS;
    }

    if (assetType?.startsWith(sectors.ELECTRICITY)) {
        return sectors.ELECTRICITY;
    }

    return null;
};

const sectorSpecificStyleRules = (feature, styleRule, sectorSpecificStyles) => {
    const { enabled, sector } = sectorSpecificStyles;
    const mapMode = styleRule.mapMode?.toUpperCase();
    const isIsolatedRule = mapMode === isolatedViewsMapMode;

    if (!enabled) {
        return !isIsolatedRule;
    }

    if (!sector) {
        return isIsolatedRule;
    }

    const featureSector = getSectorFromFeature(feature);
    const isTargetSector = featureSector === sector;

    if (isTargetSector) {
        return isIsolatedRule;
    }

    return !isIsolatedRule;
};

const evaluateStyleRule = (feature, styleRule, sectorSpecificStyles) => {
    try {
        const passesSectorSpecificRules = sectorSpecificStyleRules(feature, styleRule, sectorSpecificStyles);

        if (!passesSectorSpecificRules) {
            return false;
        }

        if (styleRule.conditions === undefined) {
            return true;
        }

        // There will only ever be one top-level condition in the array
        return evaluateCondition(feature, styleRule.conditions[0]);
    } catch (err) {
        return false;
    }
};

export const evaluateStyleRules = (feature, [...styleRules], sectorSpecificStyles = defaultSectorSpecificStyles) => {
    // Sort the style rules by priority so that the highest priority rule is evaluated first
    styleRules.sort((a, b) => b.priority - a.priority);
    for (const styleRule of styleRules) {
        const ruleSuccess = evaluateStyleRule(feature, styleRule, sectorSpecificStyles);
        if (ruleSuccess) {
            return styleRule;
        }
    }
    return undefined;
};

const TAU = Math.PI * 2;

// double modulo trick to force a positive result
export const normalize = (radians) => {
    if (radians >= 0 && radians < TAU) {
        return radians;
    }

    return ((radians % TAU) + TAU) % TAU;
};

export const toGeographic = (rotation, type) => {
    if (type === ROTATION_TYPES.arithmetic) {
        // Delta-only conversion: arithmetic and geographic use opposite positive directions.
        return normalize(-rotation);
    }
    return rotation;
};

const getRotationInRadians = (rotation, rotationUnit) => {
    return rotationUnit === ROTATION_UNITS.deg ? deg2rad(rotation) : rotation;
};

const applyRotation = (currentRotation, rotation, rotationUnit, rotationType) => {
    // careful - rotation could be 0
    if (rotation === undefined || rotation === null) {
        return currentRotation;
    }

    const rotationInRadians = getRotationInRadians(rotation, rotationUnit);
    // currentRotation is already in geographic radians from previous accumulation
    const rotationGeographic = toGeographic(rotationInRadians, rotationType);

    // return normalised sum in radians
    return normalize(currentRotation + rotationGeographic);
};

export const getGeographicRotationInRadians = (styleRuleToApply, feature) => {
    const rotations = [
        {
            value: feature?.get('initialsymbolrotation'),
            unit: feature?.get('initialsymbolrotationmeasurementunits') || ROTATION_UNITS.rad,
            type: feature?.get('initialsymbolrotationtype')
        },
        {
            // this value from the map config is either orientation or azimuth
            value: feature?.get(styleRuleToApply?.rotationPropertyName),
            unit: feature?.get(styleRuleToApply?.rotationUnitsPropertyName),
            type: feature?.get(styleRuleToApply?.rotationTypePropertyName)
        }
    ];

    // return geographic rotation in radians
    return rotations.reduce((totalRotation, { value, unit, type }) => {
        return applyRotation(totalRotation, value, unit, type);
    }, 0);
};

export const evaluateRotation = (styleRuleToApply, feature, style) => {
    const image = style?.getImage();
    if (!image) {
        return style;
    }

    const totalRotation = getGeographicRotationInRadians(styleRuleToApply, feature);
    image.setRotation(totalRotation);

    return style;
};
